import { memo } from 'react';
import { Tooltip } from '@/components/Tooltip';
import type { FieldDraft } from './schema-draft';

const MAX_FORMULA_LENGTH = 500;

const FORMULA_HELP =
  'A math expression using numbers and references to earlier Number or Calculated fields. ' +
  'Use + - * / and parentheses, and refer to other fields by their internal name (for example: qty * unitPrice). ' +
  'Empty values count as 0, and dividing by zero also shows 0. Only numbers and math operators are supported. Text, dates, and comparisons are not. ' +
  `Limits: ${MAX_FORMULA_LENGTH} characters max, 50 field references max.`;

export interface FormulaEditorProps {
  formula: string;
  /** Earlier number/computed fields available as identifiers (rule 11). */
  availableIdentifiers: FieldDraft[];
  onChange: (formula: string) => void;
}

export const FormulaEditor = memo(function FormulaEditor({
  formula,
  availableIdentifiers,
  onChange,
}: FormulaEditorProps) {
  const overLimit = formula.length > MAX_FORMULA_LENGTH;

  return (
    <div className="kstc-form-group kst-fb-formula">
      <label className="kstc-label">
        Formula <Tooltip content={FORMULA_HELP} position="right" />
      </label>
      <input
        type="text"
        className="kstc-input"
        value={formula}
        onChange={(e) => onChange(e.target.value)}
        placeholder="e.g. (qty * unitPrice) + tax"
      />
      <div className={overLimit ? 'kst-fb-error' : 'kst-fb-meta'}>
        {formula.length} / {MAX_FORMULA_LENGTH} characters
        {overLimit ? `. Shorten to under ${MAX_FORMULA_LENGTH} characters before saving.` : ''}
      </div>
      {availableIdentifiers.length > 0 ? (
        <div className="kst-fb-meta kst-fb-formula-ids">
          You can reference these earlier fields:{' '}
          {availableIdentifiers.map((f, i) => (
            <span key={f.uiId}>
              <code>{f.key}</code>
              {i < availableIdentifiers.length - 1 ? ', ' : ''}
            </span>
          ))}
        </div>
      ) : (
        <div className="kst-fb-warning">
          Add a Number or Calculated field earlier in the form first. Without one, this formula can only use plain numbers.
        </div>
      )}
    </div>
  );
});
